import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { AlertTriangle, UserMinus, UserPlus } from "lucide-react";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { SelectField, TextField } from "@/components/admin/fields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { listCustomers, updateCustomer } from "@/services/api/customers";
import {
  createCse,
  deleteCse,
  getCseOverview,
  getCse,
  listCse,
  updateCse,
  type CseOverviewCustomer,
} from "@/services/api/cse";
import type { Cse } from "@/services/api/types";

interface CseForm {
  cse_code: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
  address: string;
  parent_id: string;
  on_holiday_from: string;
  on_holiday_to: string;
}

const emptyForm: CseForm = {
  cse_code: "",
  name: "",
  surname: "",
  phone: "",
  email: "",
  address: "",
  parent_id: "",
  on_holiday_from: "",
  on_holiday_to: "",
};

/** Assign a customer to this CSE or remove an existing customer from it. */
function CseCustomerAssignment({ cse }: { cse: Cse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState("");
  const customers = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => listCustomers({ limit: 500 }),
  });
  const overview = useQuery({ queryKey: ["cse", "overview"], queryFn: getCseOverview });

  const assigned = (overview.data?.assigned ?? []).filter((c) => c.cse_id === cse.id);
  const assignedIds = new Set(assigned.map((c) => c.id));
  const available = (customers.data?.items ?? []).filter((c) => !assignedIds.has(c.id));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["cse", "overview"] });
    void queryClient.invalidateQueries({ queryKey: ["customers"] });
    void queryClient.invalidateQueries({ queryKey: ["cse"] });
  };

  const assign = useMutation({
    mutationFn: (customerId: number) => updateCustomer(customerId, { cse_id: cse.id }),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      setPending("");
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (customerId: number) => updateCustomer(customerId, { cse_id: null }),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="space-y-3 rounded-md border p-3">
      <Label className="text-sm font-semibold">{t("admin.cseCustomers")}</Label>

      {/* Customers currently on this CSE */}
      {assigned.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("common.emptyState")}</p>
      ) : (
        <ul className="space-y-1">
          {assigned.map((customer) => (
            <li key={customer.id} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{customer.name}</span>{" "}
                <span className="text-muted-foreground">{customer.customer_no}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(customer.id)}
              >
                <UserMinus className="h-4 w-4" /> {t("common.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Assign a customer to this CSE */}
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <SelectField
            label={t("admin.assignCustomer")}
            value={pending}
            onChange={setPending}
            options={[
              { value: "", label: "—" },
              ...available.map((c) => ({
                value: c.id.toString(),
                label: `${c.customer_no} — ${c.name}`,
              })),
            ]}
          />
        </div>
        <Button
          size="sm"
          disabled={!pending || assign.isPending}
          onClick={() => assign.mutate(Number(pending))}
        >
          <UserPlus className="h-4 w-4" /> {t("admin.assign")}
        </Button>
      </div>
    </div>
  );
}

export function CsePage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const query = useQuery({
    queryKey: ["cse", offset],
    queryFn: () => listCse({ limit, offset }),
  });
  const allCse = useQuery({ queryKey: ["cse", "all"], queryFn: () => listCse({ limit: 500 }) });
  const overview = useQuery({
    queryKey: ["cse", "overview"],
    queryFn: getCseOverview,
  });

  const parentOptions = (allCse.data?.items ?? []).map((c) => ({
    value: c.id.toString(),
    label: `${c.cse_code} — ${c.name} ${c.surname}`,
  }));

  const columns: ColumnDef<Cse>[] = [
    { accessorKey: "cse_code", header: t("admin.cseCode") },
    { accessorKey: "name", header: t("admin.name") },
    { accessorKey: "surname", header: t("admin.surname") },
    { accessorKey: "phone", header: t("admin.phone") },
    {
      id: "holiday",
      header: t("admin.cseHoliday"),
      cell: ({ row }) => {
        const cse = row.original;
        if (!cse.on_holiday_from && !cse.on_holiday_to) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <span className="text-xs text-muted-foreground">
            {cse.on_holiday_from ?? "…"} → {cse.on_holiday_to ?? "…"}
          </span>
        );
      },
    },
  ];

  const assignedByCse = new Map<number, CseOverviewCustomer[]>();
  for (const customer of overview.data?.assigned ?? []) {
    if (customer.cse_id == null) continue;
    const group = assignedByCse.get(customer.cse_id) ?? [];
    group.push(customer);
    assignedByCse.set(customer.cse_id, group);
  }

  return (
    <div className="space-y-8">
      <FullCrudPage<Cse, CseForm>
        title={t("nav.cse")}
        entityType="cse"
        getRecord={getCse}
        columns={columns}
        data={query.data?.items ?? []}
        total={query.data?.total ?? 0}
        isLoading={query.isLoading}
        limit={limit}
        offset={offset}
        onPageChange={setOffset}
        queryKey="cse"
        emptyForm={emptyForm}
        toForm={(r) => ({
          cse_code: r.cse_code,
          name: r.name,
          surname: r.surname,
          phone: r.phone ?? "",
          email: r.email ?? "",
          address: r.address ?? "",
          parent_id: r.parent_id?.toString() ?? "",
          on_holiday_from: r.on_holiday_from ?? "",
          on_holiday_to: r.on_holiday_to ?? "",
        })}
        canSubmit={(f) => Boolean(f.cse_code && f.name && f.surname)}
        renderForm={(form, setForm) => (
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={t("admin.cseCode")}
              value={form.cse_code}
              onChange={(v) => setForm({ ...form, cse_code: v })}
              required
            />
            <TextField
              label={t("admin.name")}
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <TextField
              label={t("admin.surname")}
              value={form.surname}
              onChange={(v) => setForm({ ...form, surname: v })}
              required
            />
            <TextField
              label={t("admin.phone")}
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
            <TextField
              label={t("admin.email")}
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
            <SelectField
              label={t("admin.parentCse")}
              value={form.parent_id}
              onChange={(v) => setForm({ ...form, parent_id: v })}
              options={[{ value: "", label: "—" }, ...parentOptions]}
            />
            <div className="col-span-2">
              <TextField
                label={t("admin.address")}
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.holidayFrom")}</Label>
              <Input
                type="date"
                value={form.on_holiday_from}
                onChange={(e) => setForm({ ...form, on_holiday_from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.holidayTo")}</Label>
              <Input
                type="date"
                value={form.on_holiday_to}
                onChange={(e) => setForm({ ...form, on_holiday_to: e.target.value })}
              />
            </div>
          </div>
        )}
        onSave={async (mode, form, record) => {
          const payload = {
            cse_code: form.cse_code,
            name: form.name,
            surname: form.surname,
            phone: form.phone || null,
            email: form.email || null,
            address: form.address || null,
            parent_id: form.parent_id ? Number(form.parent_id) : null,
            on_holiday_from: form.on_holiday_from || null,
            on_holiday_to: form.on_holiday_to || null,
          };
          return mode === "edit" && record ? updateCse(record.id, payload) : createCse(payload);
        }}
        onDelete={(record) => deleteCse(record.id)}
        recordTitle={(r) => `${r.name} ${r.surname}`}
        detailFields={(r) => [
          { label: t("admin.cseCode"), value: r.cse_code, field: "cse_code" },
          { label: t("admin.name"), value: r.name, field: "name" },
          { label: t("admin.surname"), value: r.surname, field: "surname" },
          { label: t("admin.phone"), value: r.phone, field: "phone" },
          { label: t("admin.email"), value: r.email, field: "email" },
          { label: t("admin.address"), value: r.address, field: "address" },
          {
            label: t("admin.parentCse"),
            value: allCse.data?.items.find((c) => c.id === r.parent_id)?.name,
            field: "parent_id",
          },
        ]}
        editExtras={(record) =>
          record ? <CseCustomerAssignment cse={record} /> : null
        }
      />

      {/* Overview: customers grouped by CSE (with holiday cover warnings) */}
      <div className="space-y-6">
        <div>
          <h2 className="mb-3 text-lg font-semibold">{t("admin.assignedCses")}</h2>
          {(overview.data?.cses ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("common.emptyState")}</p>
          ) : (
            <div className="space-y-4">
              {(overview.data?.cses ?? []).map((cse) => {
                const customers = assignedByCse.get(cse.id) ?? [];
                return (
                  <Card key={cse.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
                        <span>{cse.cse_code} — {cse.name} {cse.surname}</span>
                        {cse.is_active ? (
                          <Badge variant="outline">{t("admin.cseActive")}</Badge>
                        ) : (
                          <Badge variant="destructive">
                            <AlertTriangle className="h-3 w-3" /> {t("admin.cseInactive")}
                          </Badge>
                        )}
                        {cse.on_holiday_from || cse.on_holiday_to ? (
                          <span className="text-xs text-muted-foreground">
                            {t("admin.cseHoliday")}:{" "}
                            {t("admin.cseOnHolidayRange", {
                              from: cse.on_holiday_from ?? "…",
                              to: cse.on_holiday_to ?? "…",
                            })}
                          </span>
                        ) : null}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {customers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t("common.emptyState")}</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                                <th className="py-2 pr-4 font-medium">{t("admin.customer")}</th>
                                <th className="py-2 pr-4 font-medium">{t("admin.address")}</th>
                                <th className="py-2 pr-4 font-medium">{t("admin.postcode")}</th>
                                <th className="py-2 font-medium">CSE</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customers.map((customer) => (
                                <tr key={customer.id} className="border-b last:border-0">
                                  <td className="py-2 pr-4">
                                    <span className="font-medium">{customer.name}</span>
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      {customer.customer_no}
                                    </span>
                                  </td>
                                  <td className="py-2 pr-4 text-muted-foreground">
                                    {customer.address || "—"}
                                  </td>
                                  <td className="py-2 pr-4 text-muted-foreground">
                                    {customer.postcode || "—"}
                                  </td>
                                  <td className="py-2">
                                    {customer.cse_active ? (
                                      <Badge variant="outline">{t("admin.cseActive")}</Badge>
                                    ) : (
                                      <Badge variant="destructive">
                                        <AlertTriangle className="h-3 w-3" />
                                        {t("admin.cseNoActive")}
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Customers with no CSE at all */}
        <div>
          <h2 className="mb-3 text-lg font-semibold">{t("admin.unassignedCses")}</h2>
          {(overview.data?.unassigned ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("common.emptyState")}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t("admin.customer")}</th>
                    <th className="px-3 py-2 font-medium">{t("admin.address")}</th>
                    <th className="px-3 py-2 font-medium">{t("admin.postcode")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.data?.unassigned ?? []).map((customer) => (
                    <tr key={customer.id} className="border-b last:border-0">
                      <td className="px-3 py-2">
                        <span className="font-medium">{customer.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {customer.customer_no}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{customer.address || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{customer.postcode || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
